<template>
  <SideModal
    :show="show"
    @update:show="
      {
        if (!state.loading) $emit('update:show', $event);
      }
    "
    location="right"
    close-on-overlay-click
    width="500px"
  >
    <div class="modal-container">
      <div class="modal-top">
        <Typography color="primary" weight="600" size="1.25rem">Settings</Typography>
        <v-btn
          @click="
            {
              if (!state.loading) $emit('update:show', false);
            }
          "
          variant="plain"
          color="normal"
          icon
          ><v-icon color="gray600">mdi-close</v-icon></v-btn
        >
      </div>

      <div class="details">
        <div class="d-item">
          <b>School ID: </b>
          <span class="i-data">
            {{ state.school_id }}
          </span>
        </div>
        <div class="d-item">
          <b>District ID: </b>
          <span class="i-data">
            {{ state.district_id }}
          </span>
        </div>
        <div class="d-item">
          <b>School Name: </b>
          <span class="i-data">
            {{ state.school_name }}
          </span>
        </div>
        <div class="d-item">
          <b>District Name: </b>
          <span class="i-data">
            {{ state.district_name }}
          </span>
        </div>
        <div class="d-item">
          <b>PTC Account: </b>
          <span class="i-data">
            {{ state.subdomain }}
          </span>
        </div>
        <div class="d-item">
          <b>Parent Site: </b>
          <span
            class="i-data"
            style="text-overflow: ellipsis; overflow: hidden"
            data-cy="parent-site-url"
          >
            {{ state.parent_site }}
          </span>
          <div class="d-flex gap-1">
            <v-btn
              style="height: 28px; width: 28px"
              size="small"
              icon
              color="transparent"
              data-cy="parent-site-url-copy"
            >
              <v-icon @click="copyToClipboard(state.parent_site)" size="small" color="gray"
                >mdi-content-copy</v-icon
              >
            </v-btn>
            <a :href="state.parent_site" target="_blank">
              <v-btn style="height: 28px; width: 28px" size="small" icon color="transparent">
                <v-icon size="small" color="gray">mdi-open-in-new</v-icon>
              </v-btn>
            </a>
          </div>
        </div>
        <div class="d-item">
          <b>Teacher Site: </b>
          <span
            class="i-data"
            style="text-overflow: ellipsis; overflow: hidden"
            data-cy="teacher-site-url"
          >
            {{ state.teacher_site }}
          </span>
          <div>
            <v-btn
              style="height: 28px; width: 28px"
              size="small"
              icon
              color="transparent"
              data-cy="teacher-site-url-copy"
            >
              <v-icon @click="copyToClipboard(state.teacher_site)" size="small" color="gray"
                >mdi-content-copy</v-icon
              >
            </v-btn>
            <a :href="state.teacher_site" target="_blank">
              <v-btn style="height: 28px; width: 28px" size="small" icon color="transparent">
                <v-icon size="small" color="gray">mdi-open-in-new</v-icon>
              </v-btn>
            </a>
          </div>
        </div>
      </div>
      <v-divider style="margin-top: 35px; margin-bottom: 35px"></v-divider>
      <form class="settings-form" @submit.prevent="save">
        <v-textarea
          variant="outlined"
          :rows="1"
          color="primary"
          label="Welcome Message"
          v-model="state.welcome_message"
          density="compact"
          data-cy="school-welcome-message"
        ></v-textarea>
        <v-text-field
          color="primary"
          label="School Website"
          v-model="state.school_website"
          data-cy="school-website"
        ></v-text-field>

        <v-btn
          :loading="state.loading"
          type="submit"
          class="w-100"
          data-cy="school-settings-modal-save"
          :data-loading="state.loading || undefined"
          >Save</v-btn
        >
      </form>
    </div>
  </SideModal>
</template>

<script setup lang="ts">
import SideModal from "@/components/sideModal.vue";
import Typography from "@/components/typography.vue";
import { useAlertsStore } from "@/stores/alerts";
import { useSchoolStore } from "@/stores/school";
import { copyToClipboard } from "@/utils";
import { watch } from "vue";
import { reactive } from "vue";

defineProps<{
  show: boolean;
}>();

defineEmits<{
  (event: "update:show", val: boolean): void;
}>();

const schoolStore = useSchoolStore();

const state = reactive({
  loading: false,
  school_id: schoolStore.schoolDetails.data.school_id || "",
  district_id: schoolStore.schoolDetails.data.district_id || "",
  school_name: schoolStore.schoolDetails.data.school_name || "",
  district_name: schoolStore.schoolDetails.data.district?.district_name || "",
  subdomain: schoolStore.schoolDetails.data.subdomain || "",
  parent_site: window.location.origin + "/" + schoolStore.schoolDetails.data.subdomain,
  teacher_site:
    window.location.origin + "/" + schoolStore.schoolDetails.data.subdomain + "/teacher",
  welcome_message: schoolStore.schoolDetails.data.header || "",
  school_website: schoolStore.schoolDetails.data.school_url || ""
});

watch(
  () => schoolStore.schoolDetails.data,
  () =>
    Object.assign(state, {
      school_id: schoolStore.schoolDetails.data.school_id || "",
      district_id: schoolStore.schoolDetails.data.district_id || "",
      school_name: schoolStore.schoolDetails.data.school_name || "",
      district_name: schoolStore.schoolDetails.data.district?.district_name || "",
      subdomain: schoolStore.schoolDetails.data.subdomain || "",
      parent_site: window.location.origin + "/" + schoolStore.schoolDetails.data.subdomain,
      teacher_site:
        window.location.origin + "/" + schoolStore.schoolDetails.data.subdomain + "/teacher",
      welcome_message: schoolStore.schoolDetails.data.header || "",
      school_website: schoolStore.schoolDetails.data.school_url || ""
    })
);

const save = async () => {
  state.loading = true;
  const res = await schoolStore.updateSchool({
    header: state.welcome_message,
    school_url: state.school_website
  });
  state.loading = false;
  if (res) {
    useAlertsStore().addAlert("success", "School data updated");
  } else useAlertsStore().addAlert("error", "Failed to update school data");
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.modal-container {
  padding: 20px 30px;

  .modal-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .modal-body {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 30px;

    &.hidden {
      display: none;
      overflow: hidden !important;
      animation: height-transition 1s reverse forwards;
    }
  }
}

.details {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  .d-item {
    display: flex;
    gap: 5px 15px;
    align-items: center;
    flex-wrap: wrap;
    b {
      color: $gray-600;
    }
    .i-data {
      color: $gray-500;
    }
  }
}

.settings-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}
</style>
